<template>
  <v-dialog v-model="dialog" max-width="1200" persistent @input="onDialogChange">
    <v-card>
      <v-card-title class="primary white--text d-flex justify-space-between align-center">
        <span>{{ $t('dueDiligence.statusHistory') }}</span>
        <v-btn icon dark @click="closeModal">
          <v-icon style="color: white !important;">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Search and Filter Section -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              :label="$t('dueDiligence.search')"
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              clearable
            ></v-text-field>
          </v-col>
          <!-- <v-col cols="12" md="4">
            <v-select
              v-model="userRoleFilter"
              :items="userRoleOptions"
              :label="$t('dueDiligence.userRole')"
              outlined
              dense
              clearable
              @change="filterHistory"
            ></v-select>
          </v-col> -->
          <v-col cols="12" md="4">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              :label="$t('deforestation.productionPlaceStatus')"
              outlined
              dense
              clearable
              @change="filterHistory"
            ></v-select>
          </v-col>
        </v-row>

        <!-- History Table -->
        <div v-if="loading" class="text-center pa-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-2">{{ $t('dueDiligence.loadingPleaseWait') }}</div>
        </div>
        <div v-else-if="filteredHistory.length === 0" class="text-center pa-4">
          <div>{{ $t('dueDiligence.noHistoryFound') }}</div>
        </div>
        <v-data-table
          v-else
          :headers="headers"
          :items="filteredHistory"
          :loading="loading"
          :items-per-page="10"
          hide-default-footer
          class="elevation-1"
        >
          <template v-slot:item.user="{ item }">
            {{ item.user ? `${item.user.firstName} ${item.user.lastName}` : 'N/A' }}
          </template>
          <template v-slot:item.userRole="{ item }">
            {{ item.userRole || 'N/A' }}
          </template>
          <template v-slot:item.ipAddress="{ item }">
            {{ item.ipAddress || 'N/A' }}
          </template>
          <template v-slot:item.createdAt="{ item }">
            <div>{{ formatDateTime(item.createdAt) }}</div>
            <div v-if="item.expiryDate" class="text-caption text--secondary">
              {{ $t('dueDiligence.expiresOn') }}: {{ formatDateTime(item.expiryDate) }}
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <v-btn
              small
              rounded
              min-width="150"
              depressed
              :class="[getEUDRDeforestationStatus(item.status).class, 'non-clickable']"
              height="28"
            >
              {{ getEUDRDeforestationStatus(item.status).text }}
            </v-btn>
          </template>
          <template v-slot:item.activityDescription="{ item }">
            {{ item.activityDescription || 'N/A' }}
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="closeModal">
          {{ $t('dueDiligence.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { debounce } from 'lodash';
import DeforestationService from '@/_services/DeforestationService';
import moment from 'moment';

export default {
  name: 'StatusHistoryModal',
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    productionPlaceId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      historyData: [],
      filteredHistory: [],
      search: '',
      userRoleFilter: null,
      statusFilter: null,
      allUserRoles: [] // Store all unique user roles from initial fetch
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('dueDiligence.user'), value: 'user', sortable: true },
        { text: this.$t('dueDiligence.userRole'), value: 'userRole', sortable: true },
        { text: this.$t('dueDiligence.ipAddress'), value: 'ipAddress', sortable: true },
        { text: this.$t('dueDiligence.dateTime'), value: 'createdAt', sortable: true },
        { text: this.$t('deforestation.productionPlaceStatus'), value: 'status', sortable: true },
        { text: this.$t('dueDiligence.activityDescription'), value: 'activityDescription', sortable: true },
      ];
    },
    userRoleOptions() {
      return this.allUserRoles.map(role => ({ text: role, value: role }));
    },
    statusOptions() {
      return [
        { text: this.$t("deforestation.compliant"), value: 'Compliant' },
        { text: this.$t("deforestation.nonCompliant"), value: 'Non-compliant' }
      ];
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal && this.productionPlaceId) {
        this.fetchHistory(true); // Load roles on initial dialog open
      } else if (!newVal) {
        this.resetFilters();
      }
    },
    productionPlaceId(newVal) {
      if (newVal && this.dialog) {
        this.fetchHistory(true); // Load roles when production place changes
      }
    },
    search() {
      this.debouncedFetchHistory();
    },
    userRoleFilter() {
      this.fetchHistory();
    },
    statusFilter() {
      this.fetchHistory();
    }
  },
  methods: {
    async fetchHistory(loadRoles = false) {
      if (!this.productionPlaceId) {
        return;
      }

      this.loading = true;
      try {
        const filter = {
          search: this.search || undefined,
          userRole: this.userRoleFilter || undefined,
          status: this.statusFilter || undefined
        };

        const res = await DeforestationService.getProductionPlaceStatusHistory(this.productionPlaceId, filter);
        const historyRows = res.data?.getProductionPlaceStatusHistory?.rows || [];
        this.filteredHistory = historyRows;

        // Fetch all roles on initial load (when dialog opens) or when explicitly requested
        if (loadRoles && this.allUserRoles.length === 0) {
          const resAll = await DeforestationService.getProductionPlaceStatusHistory(this.productionPlaceId);
          const allRows = resAll.data?.getProductionPlaceStatusHistory?.rows || [];
          const roles = new Set(allRows.map(item => item.userRole).filter(Boolean));
          this.allUserRoles = Array.from(roles).sort();
        }
      } catch (error) {
        console.error('Error fetching production place history:', error);
        this.$notify({
          text: this.$t('dueDiligence.failedToLoadHistory'),
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    debouncedFetchHistory: debounce(function() {
      this.fetchHistory();
    }, 500),
    formatDateTime(dateStr) {
      if (!dateStr) return 'N/A';
      return moment(dateStr).format('DD-MMM-YYYY | hh:mm A');
    },
    getEUDRDeforestationStatus(status) {
      switch (status) {
        case 'Compliant':
          return { class: 'green-button', text: this.$t("deforestation.compliant") };
        case 'Non-compliant':
        case 'Non-Compliant':
          return { class: 'red-button', text: this.$t("deforestation.nonCompliant") };
        case 'Zero/Negligible Deforestation Probability':
        case 'Zero/Negligible Probability':
          return { class: 'green-button', text: this.$t("deforestation.zeroOrNegligibleProbability") };
        case 'Very High Deforestation Probability':
        case 'Very High Probability':
          return { class: 'very-high-button', text: this.$t("deforestation.veryHighProbability") };
        case 'Very Low Deforestation Probability':
        case 'Very Low Probability':
          return { class: 'very-low-button', text: this.$t("deforestation.veryLowProbability") };
        case 'High Deforestation Probability':
        case 'High Probability':
          return { class: 'red-button', text: this.$t("deforestation.highProbability") };
        case 'Low Deforestation Probability':
        case 'Low Probability':
          return { class: 'orange-button', text: this.$t("deforestation.lowProbability") };
        case 'Medium Deforestation Probability':
        case 'Medium Probability':
          return { class: 'medium-button', text: this.$t("deforestation.mediumProbability") };
        case 'Manually Deforestation Mitigated':
        case 'Manually Mitigated':
          return { class: 'manually-button', text: this.$t("dueDiligence.manualMitigated") };
        case 'unknown_error':
          return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.unknown_error") };
        case 'geo_location_error':
          return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.geo_location_error") };
        case 'region_not_available':
          return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.region_not_available") };
        case 'area_too_large':
          return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.area_too_large") };
        case 'area_too_small':
          return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.area_too_small") };
        case 'invalid_request_data':
          return { class: 'deep-orange-darken-1-button', text: this.$t("dueDiligence.invalid_request_data") };
        default:
          return { class: '', text: status || 'N/A' };
      }
    },
    resetFilters() {
      this.historyData = [];
      this.filteredHistory = [];
      this.search = '';
      this.userRoleFilter = null;
      this.statusFilter = null;
      this.allUserRoles = [];
    },
    closeModal() {
      this.$emit('close');
    },
    onDialogChange(value) {
      if (!value) {
        this.closeModal();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// Status button styles matching the global styles from main.css
::v-deep .green-button {
  background-color: #C1FFE7 !important;
  border: 2px solid #489E16 !important;
  color: #489E16 !important;
}

::v-deep .red-button {
  background-color: #FDEBEB !important;
  border: 2px solid #DA3D3D !important;
  color: #DA3D3D !important;
}

::v-deep .orange-button {
  background-color: #FFF7EC !important;
  border: 2px solid #FFA826 !important;
  color: #FFA826 !important;
}

::v-deep .yellow-button {
  background-color: #fffde7 !important;
  color: #ffeb3b !important;
}

::v-deep .manually-button {
  background-color: #E2FFF4 !important;
  border: 2px solid #00BD73 !important;
  color: #00BD73 !important;
}

::v-deep .very-high-button {
  background-color: #FF8F8F !important;
  border: 2px solid #8B0000 !important;
  color: #8B0000 !important;
}

::v-deep .very-low-button {
  background-color: #FFFCEC !important;
  border: 2px solid #F49238 !important;
  color: #F49238 !important;
}

::v-deep .medium-button {
  background-color: #FFCEB3 !important;
  border: 2px solid #F06B20 !important;
  color: #F06B20 !important;
}

::v-deep .deep-orange-darken-1-button {
  background-color: #FDEBEB !important;
  border: 2px solid #ff7b53 !important;
  color: #ff7b53 !important;
}

.non-clickable {
  pointer-events: none;
}
</style>

